import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Smartphone, Bell, Plug, ToggleLeft, ToggleRight,
  Loader2, CheckCircle, Eye, Crown, TrendingUp
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';

const INTEGRATIONS = [
  {
    key: 'payments_enabled',
    title: 'Payments',
    description: 'Enable payment processing for bookings',
    icon: CreditCard,
    color: 'text-green-400'
  },
  {
    key: 'stripe_enabled',
    title: 'Stripe',
    description: 'Stripe payment gateway and Connect payouts',
    icon: CreditCard,
    color: 'text-purple-400'
  },
  {
    key: 'subscriptions_enabled',
    title: 'Subscriptions',
    description: 'Enable owner subscription system for listing access',
    icon: Crown,
    color: 'text-amber-400'
  },
  {
    key: 'featured_ranking_enabled',
    title: 'Featured Ranking',
    description: 'Enable priority ranking for Featured/Concierge listings',
    icon: TrendingUp,
    color: 'text-coral'
  },
  {
    key: 'pwa_install_prompt_enabled',
    title: 'PWA Install Prompt',
    description: 'Show install prompt for progressive web app',
    icon: Smartphone,
    color: 'text-blue-400'
  },
  {
    key: 'sms_verification_enabled',
    title: 'SMS/Phone Verification',
    description: 'Enable phone number verification for users',
    icon: Bell,
    color: 'text-slate-400'
  },
  {
    key: 'demo_content_enabled',
    title: 'Demo Content',
    description: 'Show placeholder/demo listings to users (with DEMO badge)',
    icon: Eye,
    color: 'text-amber-500'
  }
];

const AdminIntegrationsPage = () => {
  const { getIntegrations, toggleIntegration } = useAdmin();
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const data = await getIntegrations();
      setIntegrations(data);
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, currentValue) => {
    setToggling(key);
    try {
      await toggleIntegration(key, !currentValue);
      setIntegrations(prev => ({ ...prev, [key]: !currentValue }));
      toast.success(`${key.replace(/_/g, ' ')} ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update integration');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-integrations">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">
          Integrations
        </h1>
        <p className="text-slate-400">
          Enable or disable platform features and integrations
        </p>
      </div>

      <div className="space-y-4">
        {INTEGRATIONS.map((integration) => (
          <div
            key={integration.key}
            className="bg-slate-800 rounded-xl border border-slate-700 p-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center ${integration.color}`}>
                <integration.icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white flex items-center gap-2">
                  {integration.title}
                  {integrations[integration.key] && (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  )}
                </h3>
                <p className="text-sm text-slate-400">{integration.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {toggling === integration.key ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              ) : (
                <>
                  <span className={`text-sm ${integrations[integration.key] ? 'text-green-400' : 'text-slate-500'}`}>
                    {integrations[integration.key] ? 'Enabled' : 'Disabled'}
                  </span>
                  <Switch
                    checked={integrations[integration.key]}
                    onCheckedChange={() => handleToggle(integration.key, integrations[integration.key])}
                    data-testid={`toggle-${integration.key}`}
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <p className="text-slate-400 text-sm">
          <strong className="text-white">Note:</strong> Disabling an integration will immediately affect all users. 
          Make sure to communicate changes before disabling critical features.
        </p>
      </div>
    </div>
  );
};

export default AdminIntegrationsPage;
